import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useMeasuredWidth from '../../hooks/useMeasuredWidth';
import useLinkedScrollLeft from '../../hooks/useLinkedScrollLeft';
import useDragScroll from '../../hooks/useDragScroll';
import {
  STICKY_REGION_CONTAINMENT,
  PAGE_SCROLL_PANE_X,
  ORDER_TABLE_HEADER_BG,
  STICKY_SECTION_HEADER_Z,
  STICKY_COLUMN_CELL_Z,
  STICKY_COLUMN_CORNER_Z,
} from '../../constants/stickyTable';
import SortableHeader from './SortableHeader';
import ColumnResizeHandle from './ColumnResizeHandle';
import StickyScrollbar from './StickyScrollbar';
import { PHONE_FULL_BLEED_CARD } from '../../constants/phoneLayout';
import { edgeFadeMask, readEdgeFade } from './edgeFade';

const NO_EDGE_FADE = { left: false, right: false };

/**
 * THE page-scroll table SHELL: the pinned header pane, the x-only rows pane,
 * the sticky-bottom scrollbar, the two-way `scrollLeft` link, drag-to-pan, the
 * `<colgroup>` of live widths and the sortable + resizable header cells.
 *
 * 🔀 EXTRACTED 2026-09-27, VERBATIM, out of `components/OrderTableView.js`'s
 * `OrderTableShell`, when the hires Table view (`components/hires/
 * HireTableView.js`) needed the same machinery — a second copy of this
 * function is exactly what the Code Unification Principle and
 * `npm run check-duplication` forbid. `OrderTableShell` is now a thin wrapper
 * that passes the order columns, the order width ceiling, its `order-col-resize`
 * handle prefix and its three row contexts (`wrapRows`); **its DOM is
 * byte-identical to before** — every `OrderTableView.*` test ran unchanged
 * over the extraction.
 *
 * 🔑 **IT KNOWS NOTHING ABOUT ORDERS OR HIRES.** Columns are
 * `{ key, label, width, min, align?, sortField?, fields?, ariaLabel? }` — a
 * `min` on EVERY column (root `CLAUDE.md`, `useColumnWidths`: no `min` floors
 * silently at ZERO). Rows arrive as `children` (`<tr>`s); a caller that needs
 * the measured panel width inside its rows gets it through `wrapRows(rows,
 * panelWidth)`.
 *
 * ── The scroll model (moved here from `OrderTableShell`) ──────────────
 * 🚨 **A STICKY HEADER CANNOT PIN TO THE PAGE FROM INSIDE A HORIZONTALLY
 * SCROLLABLE BOX. THE TWO ARE MUTUALLY EXCLUSIVE ON THE SAME ELEMENT.**
 * `position: sticky` resolves against the nearest SCROLL CONTAINER, and the
 * rows pane must be one (`overflow-x: auto` is what stops the PAGE scrolling
 * sideways). 🪤 `overflow-y: clip` does NOT rescue it — the element is already
 * a scroll container by virtue of its x-axis; that route shipped once and
 * parked every heading over the first rows. The height-capped inner region
 * that followed is RETIRED (owner 2026-09-26: *"a scroll within a scroll? not
 * nice UI desing"*).
 *
 * ✅ **With `stickyTop` the shell renders THREE horizontal panes** inside a
 * wrapper that is NOT a scroll container (`overflow: clip` on both axes):
 *   1. the HEADER PANE — `sticky; top: stickyTop` against the PAGE, its own
 *      x-scroller holding a header-only `<table>`;
 *   2. the ROWS — an x-only scroller (`overflow-y: hidden`, NO height cap),
 *      scrolling with the page;
 *   3. a STICKY-BOTTOM SCROLLBAR, only while the table overflows its box
 *      (2026-08-10: *"THe horizontal scroll bars iin table views on al pages
 *      should be sticky"*).
 * The three `scrollLeft`s are linked TWO-WAY by `hooks/useLinkedScrollLeft`.
 * Alignment comes from ONE width model: both tables get the same `<colgroup>`
 * and the same `minWidth`, inside panes of equal width.
 * ♿ Two `<table>`s would break the header↔cell association for a screen
 * reader, so the wrapper is `role="table"`, the header table `role="none"`
 * with explicit `rowgroup`/`row`/`columnheader` roles, and the body table
 * `role="rowgroup"` — measured in Chromium's accessibility tree as ONE table.
 * Without `stickyTop` the shell is the single-table `overflow-x-auto`
 * scrollport it always was.
 *
 * ⚠️ jsdom applies no stylesheet and does no layout, so tests assert the
 * structure, the classes and the `top`; **whether the header pins, stays
 * aligned and the page stops double-scrolling are BROWSER checks.**
 */

/**
 * The narrow-viewport query below which resize handles are withheld — a 9px
 * `touch-none` strip is not a usable touch target. Mirrors `GagGroupTable`'s
 * `NARROW_QUERY` exactly; an inline style cannot carry a Tailwind breakpoint.
 */
export const NARROW_COLUMN_RESIZE_QUERY = '(max-width: 767.98px)';

/**
 * One column's LIVE width: the stored value when it is a usable number,
 * otherwise the declared default. 🪤 `Number.isFinite`, never a truthiness or
 * `>=` test — `'120' >= 70` is true, and a string width concatenates in the
 * first `reduce`.
 */
export const columnWidthOf = (widths, c) =>
  (widths && Number.isFinite(widths[c.key]) ? widths[c.key] : c.width);

/** The live table floor: the sum of the live widths (never the defaults' sum). */
export const tableTotalWidthOf = (widths, columns) =>
  columns.reduce((sum, c) => sum + columnWidthOf(widths, c), 0);

const identityRows = (rows) => rows;
/**
 * ♿ ACCESSIBLE NAMES BY DEFAULT (2026-09-28). A labelled header is named by
 * its own TEXT (no `aria-label` — a static one would lie on a CYCLING column);
 * a column with NO heading text (the chevron column) is named "Expand". The
 * resize handle reads `label`, else `ariaLabel`, else "Expand" — so a caller
 * that forgets both can never ship "Resize undefined column", the defect both
 * order and hires tables each fixed by hand before this. A caller passing
 * `headerAriaLabel` / `resizeLabel` still overrides these.
 */
export const defaultHeaderAriaLabel = (c) => c.ariaLabel || (c.label ? undefined : 'Expand');
export const defaultResizeLabel = (c) => c.label || c.ariaLabel || 'Expand';

/**
 * 🔀 DIRTY-ROW DIMMING AS A SHARED OPTION (2026-09-28). `editingRowId` on the
 * shell means "this row id holds unsaved edits"; every OTHER row dims to
 * `EDITING_ROW_DIM`. Deliberately NO `pointer-events-none` (owner, 2026-09-28:
 * a click on another row must still reach the ask-then-continue guard).
 *
 * 🔑 WHY A CONTEXT ON THE SHELL, NOT A CLASS HELPER: the rows are the CALLER's
 * (`children` / `wrapRows`), so the shell cannot put a class on them; and a
 * helper each caller calls with its own `!== null && !==` predicate is exactly
 * the hand-written dimming that was copied between Dept Orders and the hires
 * Table. The shell owns the ONE predicate; a row asks `useEditingRowDim(id)`.
 * A row rendered outside a shell (or under one with no `editingRowId`) gets
 * `''` — which is why Approvals and My Orders, which pass nothing, are
 * unchanged (Approvals must NOT dim: `pages/CLAUDE.md`).
 */
/**
 * 🧊 A FROZEN (sticky-left) COLUMN — opt-in per column with `frozen: true`
 * (2026-09-30, the My Orders phone table's PO/Order column). The shell styles
 * the HEADER cell; the page-built ROW must put `FROZEN_COLUMN_CELL_CLASS` on
 * its own `<td>`, because the shell never touches a row it did not build.
 * Tiers are the `ui/Toolbar` z-index contract's: the body cell at
 * `STICKY_COLUMN_CELL_Z`, the header at `STICKY_COLUMN_CORNER_Z` (it is sticky
 * on both axes). Both need an OPAQUE fill or the cells scrolling under show
 * through; the header's is `gray-700` where the rest of the header row is
 * `gray-700/50` over the card — a near match, and a BROWSER check.
 * Only `left-0`: one frozen column, and it must be the FIRST.
 */
export const FROZEN_COLUMN_HEADER_CLASS =
  `sticky left-0 ${STICKY_COLUMN_CORNER_Z} bg-gray-50 dark:bg-gray-700`;
export const FROZEN_COLUMN_CELL_CLASS =
  `sticky left-0 ${STICKY_COLUMN_CELL_Z} bg-white dark:bg-gray-800`;

export const EDITING_ROW_DIM = 'opacity-40';
const EditingRowContext = createContext(null);
export function useEditingRowDim(rowId) {
  const editingRowId = useContext(EditingRowContext);
  return editingRowId !== null && editingRowId !== rowId ? EDITING_ROW_DIM : '';
}

export default function PageScrollTable({
  children,
  testIdPrefix,
  columns,
  sortField = null,
  sortDir = 'asc',
  onHeaderSort = null,
  columnWidths = null,
  onColumnResize = null,
  onColumnResetWidth = null,
  stickyTop = null,
  maxColumnWidth,
  resizeTestIdPrefix,
  headerAriaLabel = defaultHeaderAriaLabel,
  resizeLabel = defaultResizeLabel,
  wrapRows = identityRows,
  /**
   * 🔀 REGION WIDTH REPORTED UP, added 2026-09-28 for `Fit columns to
   * screen` on the order and hire tables. Optional `(panelWidth) => void`,
   * called whenever the measured scrollport width changes (mirrors
   * `panelWidth` from `wrapRows`, the SAME measurement — this is a second
   * way to READ it, never a second measurement). `GagGroupTable`'s own
   * equivalent is `onRegionWidthChange`; this is the order/hire tables'
   * route to the same number, since their rows are built by `wrapRows`
   * inside this component while the PAGE that owns the width map and
   * builds the `Actions ▾` menu sits one level up and has no other way to
   * reach it. Omit it and nothing changes — the callback is simply never
   * invoked.
   */
  onRegionWidthChange = null,
  /** The row id holding unsaved edits — see `useEditingRowDim`. `null`: none dim. */
  editingRowId = null,
  /** 📱 Opt-in: edge to edge below md (`constants/phoneLayout.js`). */
  phoneFullBleed = false,
}) {
  const bleed = phoneFullBleed ? ` ${PHONE_FULL_BLEED_CARD}` : '';
  const stickyHeader = stickyTop != null;
  const headerPaneRef = useRef(null);
  const scrollbarRef = useRef(null);
  // The rows pane IS the scrollport; `clientWidth` already excludes borders
  // and any scrollbar. 0 means "nothing to measure" (jsdom, display:none) —
  // treated as unmeasured so a CSS fallback applies.
  const [scrollportRef, measuredWidth] = useMeasuredWidth();
  const panelWidth = measuredWidth > 0 ? measuredWidth : null;
  useEffect(() => {
    if (onRegionWidthChange) onRegionWidthChange(panelWidth);
  }, [panelWidth, onRegionWidthChange]);

  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined'
      && !!window.matchMedia
      && window.matchMedia(NARROW_COLUMN_RESIZE_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(NARROW_COLUMN_RESIZE_QUERY);
    const handler = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Both callbacks or neither: a handle that can drag but not reset (or the
  // reverse) is a half-built control, and `ColumnResizeHandle` calls both.
  const resizable = typeof onColumnResize === 'function'
    && typeof onColumnResetWidth === 'function'
    && !isNarrow;

  // 🚨 The LIVE floor — freezing it at the defaults' sum makes a drag shrink
  // every column instead of growing the table.
  const totalWidth = useMemo(
    () => tableTotalWidthOf(columnWidths, columns),
    [columnWidths, columns]
  );

  // The three panes' scrollLeft, linked two-way. Rows first: they are the
  // truth on a re-render. A bare shell renders only the rows pane.
  useLinkedScrollLeft([scrollportRef, headerPaneRef, scrollbarRef]);
  // 🖱️ Click-and-drag panning (2026-09-27): a drag pans the pane it started in
  // sideways (the link moves the other two) and the PAGE vertically. Controls
  // are no-pan — `hooks/useDragScroll`. A bare shell renders no box.
  const panBoxRef = useRef(null);
  useDragScroll(panBoxRef, { axis: 'both', verticalPansWindow: true });
  // The sticky scrollbar exists only while the table is WIDER than its box.
  const overflowsX = panelWidth != null && totalWidth > panelWidth;

  // 📱 EDGE FADE (phone only, owner 2026-10-03): the shared `ui/edgeFade` mask
  // on the edge(s) with columns off-screen, tracking the sideways scroll; none
  // when nothing is hidden. Desktop (>= md) is untouched. No left fade under a
  // frozen column (it would fade the column that never moves). An expanded
  // panel (`tr[data-expanded-panel]`) is cut OUT of the rows pane's mask by an
  // opaque band over its rows — masks ADD — so the panel is never faded. A
  // mask takes no part in hit-testing: it cannot swallow a tap.
  const [fade, setFade] = useState(NO_EDGE_FADE);
  const hasFrozen = columns.some((c) => c.frozen);
  useEffect(() => {
    const el = scrollportRef.current;
    if (!isNarrow || !el) { setFade(NO_EDGE_FADE); return undefined; }
    const update = () => {
      const next = readEdgeFade(el);
      if (hasFrozen) next.left = false;
      setFade((p) => (p.left === next.left && p.right === next.right ? p : next));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isNarrow, hasFrozen, panelWidth, totalWidth, scrollportRef]);
  const fading = fade.left || fade.right;
  const [panelBands, setPanelBands] = useState(null);
  useEffect(() => {
    const el = scrollportRef.current;
    if (!fading || !el) { setPanelBands(null); return undefined; }
    const measure = () => {
      const top0 = el.getBoundingClientRect().top;
      const bands = Array.from(el.querySelectorAll('tr[data-expanded-panel]')).map((tr) => {
        const r = tr.getBoundingClientRect();
        const a = Math.round(r.top - top0);
        const b = Math.round(r.bottom - top0);
        return `linear-gradient(to bottom, transparent ${a}px, #000 ${a}px, #000 ${b}px, transparent ${b}px)`;
      });
      const next = bands.length ? bands.join(', ') : null;
      setPanelBands((p) => (p === next ? p : next));
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && el.firstElementChild) {
      ro = new ResizeObserver(measure);
      ro.observe(el.firstElementChild);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [fading, children, scrollportRef]);
  const fadeStyle = (withBands) => {
    if (!fading) return null;
    const mask = [edgeFadeMask(fade), withBands ? panelBands : null].filter(Boolean).join(', ');
    return { WebkitMaskImage: mask, maskImage: mask };
  };
  const fadeAttr = fading
    ? `${fade.left ? 'left' : ''}${fade.left && fade.right ? ' ' : ''}${fade.right ? 'right' : ''}`
    : undefined;

  const colgroup = (
    <colgroup>
      {columns.map((c) => (
        <col key={c.key} style={{ width: columnWidthOf(columnWidths, c) }} />
      ))}
    </colgroup>
  );

  const headerCells = columns.map((c) => (
    // NO `sticky` on the cell: in the page-scroll layout the whole header PANE
    // is the sticky element. `role` only there — the header `<table>` is
    // `role="none"`, which would strip the cell's native role.
    <SortableHeader
      key={c.key}
      label={c.label}
      sortId={c.sortField || null}
      fields={c.fields}
      activeSort={sortField}
      sortDir={sortDir}
      onSort={onHeaderSort}
      align={c.align === 'right' ? 'right' : 'left'}
      className={c.frozen ? FROZEN_COLUMN_HEADER_CLASS : ''}
      role={stickyHeader ? 'columnheader' : undefined}
      testId={`${testIdPrefix}-sort-header-${c.key}`}
      ariaLabel={headerAriaLabel(c)}
    >
      {/* 🚨 A CHILD OF THE `<th>`, A SIBLING OF THE SORT BUTTON — a handle
          inside the `w-full` button would bubble every pointerup into the sort
          handler. See `ui/SortableHeader` note 8. */}
      {resizable ? (
        <ColumnResizeHandle
          columnKey={c.key}
          label={resizeLabel(c)}
          width={columnWidthOf(columnWidths, c)}
          min={c.min}
          max={maxColumnWidth}
          totalWidth={totalWidth}
          // 🪤 NO `- 2`: the rows pane IS the scrollport (unlike GagGroupTable,
          // which measures an outer card with a 1px border each side).
          scrollportWidth={panelWidth}
          onResize={onColumnResize}
          onReset={onColumnResetWidth}
          testIdPrefix={resizeTestIdPrefix}
        />
      ) : null}
    </SortableHeader>
  ));

  const body = (
    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
      <EditingRowContext.Provider value={editingRowId}>
        {wrapRows(children, panelWidth)}
      </EditingRowContext.Provider>
    </tbody>
  );

  // `<prefix>-table-view` on the OUTERMOST element in both layouts.
  const viewTestId = `${testIdPrefix}-table-view`;
  // `pst-table`: the hook for the phone tight-cell rule in index.css
  // (`constants/phoneLayout.js` → PHONE_CELL_PAD_SAVING). Only THIS shell's
  // own tables carry it, so a table nested in an expanded panel is untouched.
  const tableClass = 'pst-table w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700';
  const tableStyle = { minWidth: totalWidth };

  if (!stickyHeader) {
    return (
      <div
        ref={scrollportRef}
        className={`overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800${bleed}`}
        style={fadeStyle(true) || undefined}
        data-fade={fadeAttr}
        data-testid={viewTestId}
      >
        <table className={tableClass} style={tableStyle}>
          {colgroup}
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>{headerCells}</tr>
          </thead>
          {body}
        </table>
      </div>
    );
  }

  return (
    <div
      ref={panBoxRef}
      // ♿ ONE accessible table over two DOM tables — see the docblock.
      role="table"
      // 🔑 `overflow-clip` is NOT a scroll container, so the header pane's
      // `sticky` resolves against the PAGE; it only clips the corners.
      // 🚨 `STICKY_REGION_CONTAINMENT` (`relative`) keeps every absolute
      // descendant (the `sr-only` spans above all) from resolving against the
      // initial containing block and inflating the document.
      className={`${STICKY_REGION_CONTAINMENT} pst-shell overflow-clip rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800${bleed}`}
      data-testid={viewTestId}
    >
      <div
        ref={headerPaneRef}
        // Pins BENEATH THE TOOLBAR against the page. `z-10` is the section
        // tier of the Toolbar z-index contract. The fill is on the PANE
        // because the pane is what moves.
        className={`sticky ${STICKY_SECTION_HEADER_Z} ${PAGE_SCROLL_PANE_X} ${ORDER_TABLE_HEADER_BG}`}
        style={{ top: stickyTop, ...fadeStyle(false) }}
        data-page-scroll-pane=""
        data-testid={`${testIdPrefix}-table-header`}
      >
        <table role="none" className={tableClass} style={tableStyle}>
          {colgroup}
          <thead role="rowgroup" className="bg-gray-50 dark:bg-gray-700/50">
            <tr role="row">{headerCells}</tr>
          </thead>
        </table>
      </div>
      <div
        ref={scrollportRef}
        // The ROWS: x-only on the PAGE's vertical scroll — never `overflow-y:
        // auto`, nothing here may become a second vertical scroller.
        className={`${STICKY_REGION_CONTAINMENT} ${PAGE_SCROLL_PANE_X}`}
        style={fadeStyle(true) || undefined}
        data-fade={fadeAttr}
        data-page-scroll-pane=""
        data-testid={`${testIdPrefix}-table-rows`}
      >
        <table role="rowgroup" className={tableClass} style={tableStyle}>
          {colgroup}
          {body}
        </table>
      </div>
      {overflowsX ? (
        <StickyScrollbar
          ref={scrollbarRef}
          contentStyle={tableStyle}
          testId={`${testIdPrefix}-table-scrollbar`}
        />
      ) : null}
    </div>
  );
}
